import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {
  QA_MODALITY_PARAM,
  QA_MODALITY_STORAGE_KEY,
  readModalityOverride,
  type Glp1Modality,
} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {readSeverityOverride, SEVERITIES, SEVERITY_PARAM, type ApneaSeverity} from 'surveys/osa_funnel/survey/severity';
import styles from './OsaQaMenu.module.css';

/**
 * QA affordance for the OSA funnel, ported from rise-web-funnel v2's QAFab:
 * a floating purple FAB that toggles a dark popup menu (instead of jumping
 * straight to the admin screen), with a toast for feedback. Actions are
 * adapted to this funnel — route jumps, modality/severity overrides, and a
 * survey reset. Rendered only on /osa routes (see QABar).
 */

const MODALITIES: {value: Glp1Modality; label: string}[] = [
  {value: 'branded_v1', label: 'branded_v1 (Zepbound)'},
  {value: 'compounded_v1', label: 'compounded_v1 (semaglutide)'},
];

/** Build a URL on the current path with one search param overridden. */
const urlWithParam = (name: string, value: string): string => {
  const params = new URLSearchParams(window.location.search);
  params.set(name, value);
  return `${window.location.pathname}?${params.toString()}`;
};

export const OsaQaMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  // The currently *forced* override (null when none) — so a radio is filled
  // only when there's an actual override, not just the default. Recomputed
  // on route change rather than every render (each read touches URL +
  // localStorage). The readers use window.location.search (which mirrors
  // location.search), so the dep is intentional but invisible to the linter.
  const [activeModality, activeSeverity] = useMemo<[Glp1Modality | null, ApneaSeverity | null]>(
    () => [readModalityOverride(), readSeverityOverride()],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  );

  // Clear the toast timer on unmount / before scheduling a new one so a
  // stale timer can't setState after unmount or stomp a newer toast.
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (message: string) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1500);
  };

  // Route jumps stay in-app and preserve the current query (modality, etc.).
  const goTo = (path: string) => {
    setOpen(false);
    navigate(`${path}${location.search}`);
  };

  // Modality/severity changes update the URL param and full-reload so the
  // survey + hooks re-initialise with the new value. The saved survey
  // position is restored from localStorage, so this keeps the current screen.
  const selectModality = (value: Glp1Modality) => {
    try {
      window.localStorage.setItem(QA_MODALITY_STORAGE_KEY, value);
    } catch {
      // localStorage may throw under privacy modes; the URL param still applies.
    }
    window.location.assign(urlWithParam(QA_MODALITY_PARAM, value));
  };

  const selectSeverity = (value: ApneaSeverity) => {
    window.location.assign(urlWithParam(SEVERITY_PARAM, value));
  };

  const handleReset = () => {
    SurveyData.clear();
    try {
      window.localStorage.removeItem(QA_MODALITY_STORAGE_KEY);
    } catch {
      // ignore
    }
    setOpen(false);
    showToast('Survey reset');
    // Full reload to drop in-memory survey state, back to the funnel start.
    window.location.assign(`/osa/start${location.search}`);
  };

  return (
    <>
      {toast && <div className={styles.toast}>{toast}</div>}
      {open && (
        <div className={styles.menu}>
          <div className={styles.sectionLabel}>Navigate</div>
          <button type="button" className={styles.menuItem} onClick={() => goTo('/osa/start')}>
            Start
          </button>
          <button type="button" className={styles.menuItem} onClick={() => goTo('/osa/checkout')}>
            Paywall
          </button>
          <button type="button" className={styles.menuItem} onClick={() => goTo('/osa/screening')}>
            Medical screening
          </button>
          <button
            type="button"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              navigate('/devqatools');
            }}
          >
            Admin tools
          </button>

          <div className={styles.divider} />
          <div className={styles.sectionLabel}>Modality</div>
          {MODALITIES.map(m => {
            const selected = m.value === activeModality;
            return (
              <button
                key={m.value}
                type="button"
                className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                onClick={() => selectModality(m.value)}
              >
                <span className={styles.radio}>{selected ? '●' : '○'}</span>
                <span className={styles.optionLabel}>{m.label}</span>
              </button>
            );
          })}

          <div className={styles.divider} />
          <div className={styles.sectionLabel}>Severity</div>
          {SEVERITIES.map(s => {
            const selected = s === activeSeverity;
            return (
              <button
                key={s}
                type="button"
                className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                onClick={() => selectSeverity(s)}
              >
                <span className={styles.radio}>{selected ? '●' : '○'}</span>
                <span className={styles.optionLabel}>{s}</span>
              </button>
            );
          })}

          <div className={styles.divider} />
          <button type="button" className={styles.menuItemDanger} onClick={handleReset}>
            Reset Survey
          </button>
        </div>
      )}
      <button type="button" aria-label="QA tools" className={styles.fab} onClick={() => setOpen(prev => !prev)}>
        QA
      </button>
    </>
  );
};
